import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../../components/ui/Button/Button.jsx";
import HelpSupportModal from "../../components/ui/HelpSupportModal/HelpSupportModal.jsx";
import { useApp } from "../../hooks/useApp.js";
import { useTotals } from "../../hooks/useTotals.js";
import { money } from "../../utils/format.js";
import { getServiceFeeReceipt, downloadServiceFeeReceiptPdf } from "../../services/paymentDetailsService.js";
import { payInstructions, payNowLink } from "../../utils/paymentMethods.js";
import ShareBox, { SHARE_MESSAGES } from "../../components/share/ShareBox.jsx";
import { getReferralSummary } from "../../services/engagementService";
import { getToken } from "../../services/api.js";

// Reached when ReviewStep's XML-generation call fails (e.g. the IRS schema
// rejects the return) - shown instead of the success screen below so the
// filer isn't left staring at a "Continue to payment" button that can never
// succeed for this return without help. The raw schema-validation error
// (`message`) is intentionally not rendered - it's meaningful to us, not to
// a filer, so this screen just points them at support instead.
function GenerationError({ returnId, onBack }) {
  const [supportOpen, setSupportOpen] = useState(false);

  return (
    <div className="max-w-[1180px] mx-auto px-[22px] py-[26px] pb-[70px]">
      <div className="max-w-[640px] mx-auto text-center">
        <div className="w-24 h-24 rounded-full bg-[var(--danger-soft,#fee2e2)] text-[var(--danger)] grid place-items-center text-[46px] mx-auto mb-4.5">
          !
        </div>
        <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--danger)] font-semibold">
          We hit a problem
        </div>
        <h1 className="text-[32px] leading-[1.1] my-1.5">We couldn't generate your return.</h1>
        <p className="text-[var(--muted)] mx-auto mb-2">
          Your return hasn't been filed and no payment was taken{returnId ? <> (return #{returnId})</> : null}.
          Please contact our support team for help resolving this before trying again.
        </p>

        <div className="flex gap-3 justify-center mt-6 flex-wrap">
          <Button size="lg" onClick={() => setSupportOpen(true)}>
            ✉ Contact support
          </Button>
          <Button variant="ghost" size="lg" onClick={onBack}>
            ← Back to review
          </Button>
        </div>
      </div>
      <HelpSupportModal open={supportOpen} onClose={() => setSupportOpen(false)} />
    </div>
  );
}

// Reached when viewing (from My Filings) a return the IRS rejected - the
// success screen below (checkmark, "filed", stamped Schedule 1 download)
// would be actively wrong here, since a rejected return has no stamped
// Schedule 1 and isn't currently editable (TaxReturn.isDraft() on the
// backend excludes "rejected").
function RejectedNotice({ returnId, businessName, onBack }) {
  const [supportOpen, setSupportOpen] = useState(false);

  return (
    <div className="max-w-[1180px] mx-auto px-[22px] py-[26px] pb-[70px]">
      <div className="max-w-[640px] mx-auto text-center">
        <div className="w-24 h-24 rounded-full bg-[var(--danger-soft,#fee2e2)] text-[var(--danger)] grid place-items-center text-[46px] mx-auto mb-4.5">
          ✕
        </div>
        <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--danger)] font-semibold">
          Rejected by the IRS
        </div>
        <h1 className="text-[32px] leading-[1.1] my-1.5">This return wasn't accepted.</h1>
        <p className="text-[var(--muted)] mx-auto mb-2">
          Form 2290 for <b>{businessName || "your business"}</b> was submitted but rejected by the IRS. Contact our
          support team for help resolving this before it can be resubmitted.
        </p>

        <div className="flex gap-3 justify-center mt-6 flex-wrap">
          <Button size="lg" onClick={() => setSupportOpen(true)}>
            ✉ Contact support
          </Button>
          <Button variant="ghost" size="lg" onClick={onBack}>
            ← Back to My Filings
          </Button>
        </div>
      </div>
      <HelpSupportModal open={supportOpen} onClose={() => setSupportOpen(false)} />
    </div>
  );
}

const Confirmation = () => {
  const { state } = useApp();
  const t = useTotals();
  const navigate = useNavigate();
  const location = useLocation();
  const generationError = location.state?.error;
  // Set by ServiceFeeStep/PaymentDetailsStep's submitReturn() result right
  // after filing, or seeded by MyFilings' "View" action for a past filing.
  // Defaults to "submitted" (rather than treating it as unknown) since
  // that's what a fresh, just-filed return's status actually is.
  const filedStatus = state.filedResult?.status || "submitted";
  const accepted = filedStatus === "accepted";
  const rejected = filedStatus === "rejected";
  // Keyed on the IRS payment method chosen in the Pay step, NOT on the service fee.
  const taxPayGuidance = payInstructions(state.pay?.method, { amountLabel: money(t.line6) });
  const taxPayLink = payNowLink(state.pay?.method);
  const [receipt, setReceipt] = useState(null);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  // Share box only - no rating here: the IRS hasn't accepted the return yet, so the
  // rating prompt waits for the stamped Schedule 1 (My Filings / dashboard).
  // The referral summary gives the share link its code and the share box its credit amount
  // and yearly limit (both come from the server, as on the dashboard). If it can't be loaded,
  // or the filer has already earned this period's limit, the box falls back to the plain
  // "share the free tools" wording and shares without a code.
  const [referral, setReferral] = useState(null);
  useEffect(() => {
    if (!getToken()) return undefined;
    let active = true;
    getReferralSummary()
      .then((s) => {
        if (active) setReferral(s || null);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);
  const refCode = referral?.code || null;
  const canEarnCredit =
    !!refCode && !!referral?.creditValue && referral.earnedThisTaxPeriod < referral.capPerTaxPeriod;

  // Same receipt shown on the Payments page (getServiceFeeReceipt backs
  // PaymentReceiptModal's rows) - reused here instead of the stamped
  // Schedule 1 card. `paymentId` is a ServiceFeePayment id for a Stripe-paid
  // return, or a SubscriptionReturnUsage id (source=SUBSCRIPTION_CREDIT) for
  // a credit-paid one - PaymentDetailsStep sets both fields to match whichever
  // path the filer actually went through.
  const receiptSource = state.serviceFee?.source;
  useEffect(() => {
    const paymentId = state.serviceFee?.paymentId;
    if (!paymentId) return;
    let active = true;
    (async () => {
      try {
        const data = await getServiceFeeReceipt(paymentId, receiptSource);
        if (active) setReceipt(data);
      } catch {
        /* receipt details card just renders empty */
      }
    })();
    return () => {
      active = false;
    };
  }, [state.serviceFee?.paymentId, receiptSource]);

  // Same download the Payments page's receipt modal uses (downloadServiceFeeReceiptPdf).
  const handleDownloadReceipt = async () => {
    const paymentId = state.serviceFee?.paymentId;
    if (!paymentId) return;
    setDownloadingReceipt(true);
    try {
      const { blob, filename } = await downloadServiceFeeReceiptPdf(paymentId, receiptSource);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      /* the button just stays clickable - no toast plumbing on this page */
    } finally {
      setDownloadingReceipt(false);
    }
  };

  if (generationError) {
    return <GenerationError returnId={state.returnId} onBack={() => navigate(-1)} />;
  }

  if (rejected) {
    return (
      <RejectedNotice
        returnId={state.returnId}
        businessName={state.biz.name}
        onBack={() => navigate("/dashboard/filings")}
      />
    );
  }

  return (
    <div className="max-w-[1180px] mx-auto px-[22px] py-[26px] pb-[70px]">
      <div className="max-w-[640px] mx-auto text-center">
        <div className="w-24 h-24 rounded-full bg-[var(--accent-soft)] text-[var(--accent)] grid place-items-center text-[46px] mx-auto mb-4.5">
          ✓
        </div>
        <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
          {accepted ? "Accepted by the IRS" : "Submitted to the IRS"}
        </div>
        <h1 className="text-[32px] leading-[1.1] my-1.5">
          {accepted ? "Your return was accepted." : "Your return is filed."}
        </h1>
        <p className="text-[var(--muted)] mx-auto mb-2">
          Form 2290 for <b>{state.biz.name || "your business"}</b> was generated, validated against the IRS 2290
          schema, and submitted, with <b className="font-mono">{money(t.line6)}</b> in HVUT due.
          {state.filedResult?.submissionId ? (
            <> Submission ID <b className="font-mono">{state.filedResult.submissionId}</b>.</>
          ) : null}
        </p>

        {/* What the filer still owes the IRS. The service-fee receipt below shows a
            successful payment TO US, which reads as "everything is paid" — for EFTPS
            and card it is not, and the tax is the filer's own legal obligation.
            Gated on an actual balance: a suspended-vehicle-only return owes nothing. */}
        {t.line6 > 0 && taxPayGuidance && (
          <div
            className={`max-w-[500px] mx-auto mt-6 rounded-[16px] border p-5 text-left ${
              taxPayGuidance.settled
                ? "bg-[var(--accent-soft)] border-[var(--accent)]"
                : "bg-[var(--amber-soft)] border-[var(--amber)]"
            }`}
          >
            <div className="text-[14px] font-bold mb-1.5">{taxPayGuidance.heading}</div>
            <p className="text-[13px] text-[var(--muted)] m-0">{taxPayGuidance.body}</p>
            {taxPayLink && (
              <a
                href={taxPayLink.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-3 text-[13px] font-semibold text-[var(--accent)] underline"
              >
                {taxPayLink.label}
              </a>
            )}
          </div>
        )}

        {receipt && (
          <div className="max-w-[500px] mx-auto mt-6 bg-[var(--panel)] rounded-[16px] border border-[var(--line)] overflow-hidden text-left">
            <div className="py-3 px-5 border-b border-[var(--line)] text-[11px] font-bold text-[var(--muted)] uppercase tracking-[0.06em]">
              Receipt Details
            </div>
            <div className="py-1 px-5">
              {[
                [
                  "Confirmation #",
                  <span className="font-mono font-semibold text-[var(--accent)]">
                    {receipt.confirmationNumber || "—"}
                  </span>,
                ],
                ["Description", receipt.description || "Form 2290 e-filing service fee"],
                // ["Date", fmtDate(receipt.date)],
                ["Service fee", money(receipt.serviceFeeAmount)],
                ...(Number(receipt.referralCreditAmount) > 0
                  ? [["Referral credit", `-${money(receipt.referralCreditAmount)}`]]
                  : []),
                ...(Number(receipt.processingFeeAmount) > 0
                  ? [["Processing fee", money(receipt.processingFeeAmount)]]
                  : []),
                ["Total", <span className="font-bold text-[var(--ink)]">{money(receipt.amount)}</span>],
                [
                  "Status",
                  <span
                    className="text-[12px] font-bold py-[2px] px-2 rounded-[20px]"
                    style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
                  >
                    {receipt.status}
                  </span>,
                ],
                ["Payment method", receipt.paymentMethod || "—"],
              ].map(([label, val], i) => (
                <div
                  key={i}
                  className="flex justify-between items-start py-2 border-b border-[var(--line)] last:border-b-0 text-[13px]"
                >
                  <span className="text-[var(--muted)] shrink-0">{label}</span>
                  <span className="text-[var(--ink)] text-right">{val}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-3 justify-center mt-6 flex-wrap">
          {state.serviceFee?.paymentId && (
            <Button variant="ghost" size="lg" onClick={handleDownloadReceipt} disabled={downloadingReceipt}>
              {downloadingReceipt ? "Downloading…" : "⬇ Download Receipt"}
            </Button>
          )}
          <Button size="lg" onClick={() => navigate("/dashboard/filings")}>
            Back to My Filings
          </Button>
        </div>
        <div className="max-w-[500px] mx-auto mt-8">
          <ShareBox
            refCode={refCode}
            placement="confirmation"
            title={
              canEarnCredit
                ? `Get ${money(referral.creditValue)} off your next filing`
                : "Know someone with a truck over 55,000 lbs?"
            }
            intro={
              canEarnCredit
                ? `Share your link. When a friend's first return is accepted by the IRS, you get a ${money(
                    referral.creditValue
                  )} credit - the service fee for one truck. Up to ${referral.capPerTaxPeriod} a year, and they stack.`
                : "Share where you file with your group, forum or association chat."
            }
            messages={SHARE_MESSAGES}
          />
        </div>

        <p className="text-[var(--dim)] text-xs mt-[22px] text-center">
          {accepted
            ? "The return XML was validated against the IRS 2290 schema (TY2026) and accepted by the IRS."
            : "The return XML was validated against the IRS 2290 schema (TY2026) and submitted. IRS transmission and the stamped Schedule 1 acknowledgment are pending."}
        </p>
      </div>
    </div>
  );
};

export default Confirmation;
